/**
 * UnitPicker — searchable unit link for the work-report form (C3).
 *
 * Fifth thin wrapper over the shared ``AsyncCombobox`` (C0). It owns only
 * the unit vocabulary and delegates debounce, paging, stale-response
 * protection, keyboard and theming to the base.
 *
 * Replaces a native <select> filled with a bare ``fetchAdminUnits()``,
 * which sent no parameters at all and therefore silently received the
 * server's default page of 50 — unit 51 was unreachable, and an edit
 * form could not display a link outside that page.
 *
 * Rendered shape:
 *   Madrid · 4-Zimmer-Wohnung
 *   Beispielstrasse 1, 8000 Musterstadt
 *
 * Server contract:
 *   GET /api/admin/units?q=…&limit&skip   (``q`` added in C3)
 *   GET /api/admin/units/{id}             for hydrating the current link
 *
 * The payload it feeds back is unchanged: a bare ``unit_id`` string.
 */
import React from "react";

import AsyncCombobox from "../../../../components/ui/AsyncCombobox";
import { fetchAdminUnit, fetchAdminUnitsPage } from "../../../../api/adminData";

const PAGE_SIZE = 20;

/**
 * "Madrid · 4-Zimmer-Wohnung" — short code first because that is what
 * staff say out loud. Falls back to the title alone when a unit has no
 * code (both are then the same string, so it is not repeated), and never
 * renders a UUID.
 * Exported for tests.
 */
export function unitLabel(unit) {
  const code = (unit?.short_unit_id || "").trim();
  const title = (unit?.title || unit?.name || "").trim();
  if (code && title && code !== title) return `${code} · ${title}`;
  return code || title || "Unit ohne Bezeichnung";
}

/**
 * "Liegenschaft · Beispielstrasse 1, 8000 Musterstadt" — the building
 * name when the unit belongs to one, then the postal address. Segments
 * that are missing are dropped rather than rendered as empty separators.
 * Exported for tests.
 */
export function unitLocation(unit) {
  const street = (unit?.address || "").trim();
  const city = (unit?.city || "").trim();
  const address = [street, city].filter(Boolean).join(", ") || null;
  return (
    [(unit?.property_title || "").trim() || null, address]
      .filter(Boolean)
      .join(" · ") || null
  );
}

export default function UnitPicker({ value, onChange, disabled = false }) {
  const [selected, setSelected] = React.useState(null);

  // Hydrate the CURRENT link by id (edit mode, and C1.1 prefill from a
  // task). Without this a unit past page 1 renders as "nothing selected"
  // and a save could drop the link.
  React.useEffect(() => {
    if (!value) {
      setSelected(null);
      return undefined;
    }
    if (selected && String(selected.id) === String(value)) return undefined;
    let cancelled = false;
    fetchAdminUnit(value)
      .then((unit) => {
        if (!cancelled && unit) setSelected(unit);
      })
      .catch(() => {
        // A deleted or inaccessible unit must not break the form.
      });
    return () => {
      cancelled = true;
    };
  }, [value, selected]);

  // NOTE: ``fetchAdminUnitsPage``, not ``fetchAdminUnits`` — the latter
  // takes no parameters and unwraps the envelope to a bare array.
  const fetchPage = React.useCallback(
    ({ query, offset, limit }) =>
      fetchAdminUnitsPage({ q: query || undefined, limit, offset }),
    []
  );

  const handleChange = React.useCallback(
    (id, item) => {
      setSelected(item);
      onChange(id);
    },
    [onChange]
  );

  return (
    <AsyncCombobox
      label="Einheit (optional)"
      placeholder="— keine —"
      searchPlaceholder="Unit nach Kurzcode, Titel, Adresse oder Liegenschaft suchen …"
      loadingText="Units werden geladen …"
      emptyText="Keine passenden Units gefunden."
      errorText="Units konnten nicht geladen werden."
      value={value}
      selectedItem={selected}
      onChange={handleChange}
      fetchPage={fetchPage}
      getItemId={(u) => u.id}
      getItemLabel={(u) => unitLabel(u)}
      pageSize={PAGE_SIZE}
      disabled={disabled}
      renderItem={(unit) => (
        <span className="block min-w-0">
          <span
            title={unitLabel(unit)}
            className="block truncate text-[13px] font-medium text-foreground"
          >
            {unitLabel(unit)}
          </span>
          {unitLocation(unit) ? (
            <span
              title={unitLocation(unit)}
              className="block truncate text-[11px] text-muted-foreground"
            >
              {unitLocation(unit)}
            </span>
          ) : null}
        </span>
      )}
    />
  );
}
